<script setup lang="ts">
import {onMounted, ref, watchEffect} from "vue";
import {onLongPress} from "@vueuse/core";
import {Button, Popover} from "../../Library";
import {
    deleteReaction as apiDeleteReaction,
    storeReaction,
    updateReaction as apiUpdateReaction,
} from "../../../Services/reactions";
import type {AllowedReactions} from "../../../Types";

type Props = {
    reactions?: Array<{
        reaction: AllowedReactions;
        count: number;
    }>;
    reacted: AllowedReactions | null;
    size?: number;
    comment?: any;
    disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    reactions: () => [],
    size: 18,
});

const emit = defineEmits(["reacted", "unreacted"]);

const allowedReactions: Array<AllowedReactions> = ["👍", "🥳", "👀", "😍", "💅"];

const reacted = ref(props.reacted);
const existingReactions = ref(props.reactions);
watchEffect(() => {
    existingReactions.value = props.reactions;
});
watchEffect(() => {
    reacted.value = props.reacted;
});

const reactionButtonRef = ref<HTMLButtonElement | null>(null);
const newReactionPopover = ref<Popover>();

const postReaction = async (reaction: AllowedReactions) => {
    console.log(props.comment);
    if (existingReactions.value.find((r) => r.reaction === reaction)) {
        existingReactions.value.find((r) => r.reaction === reaction)!.count++;
    } else {
        existingReactions.value.push({reaction, count: 1});
    }

    reacted.value = reaction;
    await storeReaction(props.comment.group_id, props.comment.id, {reaction: reaction});
    emit("reacted", reaction);
};
const updateReaction = async (reaction: AllowedReactions, reactionId: number) => {
    existingReactions.value.find((r) => r.reaction === reacted.value)!.count--;
    if (existingReactions.value.find((r) => r.reaction === reaction)) {
        existingReactions.value.find((r) => r.reaction === reaction)!.count++;
    } else {
        existingReactions.value.push({reaction, count: 1});
    }
    reacted.value = reaction;
    await apiUpdateReaction(props.comment.group_id, props.comment.id, reactionId, {reaction: reaction});
    emit("reacted", reaction);
};
const deleteReaction = async (reaction: AllowedReactions, reactionId: number) => {
    existingReactions.value.find((r) => r.reaction === reaction)!.count--;
    reacted.value = null;
    await apiDeleteReaction(props.comment.group_id, props.comment.id, reactionId, {reaction: reaction});
    emit("unreacted", reacted.value);
};

const handleReaction = async (reaction: AllowedReactions) => {
    if (!props.comment.id) {
        return;
    }

    console.log(reacted.value, reaction);

    if (reacted.value === reaction) {
        await deleteReaction(reaction, 1); // TODO: Placeholder for reactionId
        return;
    }

    // If user has already reacted, change the reaction
    if (reacted.value && reacted.value !== reaction) {
        await updateReaction(reaction, 1); // TODO: Placeholder for reactionId
    }

    if (!reacted.value) {
        await postReaction(reaction);
    }

    newReactionPopover.value?.hide();
};

const dismissable = ref(false);
onLongPress(reactionButtonRef, (e) => newReactionPopover.value?.toggle(e, e.target), {
    distanceThreshold: 24,
    delay: 500,
    onMouseUp: async (duration, distance, isLongPress) => {
        if (isLongPress) {
            setTimeout(() => {
                dismissable.value = true;
            }, 0);
            return;
        }
        handleReaction("👍");
    },
});

onMounted(() => {
    existingReactions.value.some((r) => r.reaction === "👍") ||
        existingReactions.value.push({reaction: "👍", count: 0});
});
</script>
<template>
    <div class="flex items-center gap-2">
        <Button
            ref="reactionButtonRef"
            class="border !px-2 !py-0 !font-normal hover:brightness-90 dark:border-stone-700"
            :class="{
                'border-askr-green-500 bg-askr-green-50 text-askr-green-500 dark:bg-askr-green-800': reacted === '👍',
                'bg-stone-50 dark:bg-stone-800': reacted !== '👍',
            }"
            :disabled="disabled"
        >
            👍 {{ existingReactions.find((r) => r.reaction === "👍")?.count ?? 0 }}
        </Button>
        <template v-for="{reaction, count} in existingReactions" :key="reaction">
            <Button
                v-if="count && reaction !== '👍'"
                class="border !px-2 !py-0 !font-normal hover:brightness-90 dark:border-stone-700"
                :class="{
                    'border-askr-green-500 bg-askr-green-50 text-askr-green-500 dark:bg-askr-green-800':
                        reacted === reaction,
                    'bg-stone-50 dark:bg-stone-800': reacted !== reaction,
                }"
                @click="handleReaction(reaction)"
                :disabled="disabled"
            >
                {{ reaction }} {{ count }}
            </Button>
        </template>
        <Popover
            ref="newReactionPopover"
            class="!rounded-full border-none bg-transparent shadow-none dark:bg-transparent"
            pt:content:class="!p-0 !rounded-full"
            :dismissable="dismissable"
            @hide="dismissable = false"
        >
            <div class="flex items-center gap-0.5">
                <template v-for="(reaction, i) in allowedReactions" :key="i">
                    <Button
                        class="border-stone--300 bor-der bg-stone-100 !px-2.5 !py-1 text-xl shadow-lg shadow-stone-500 hover:brightness-110 dark:bg-stone-800 dark:shadow-black"
                        @click="handleReaction(reaction)"
                        :disabled="disabled"
                    >
                        {{ reaction }}
                    </Button>
                </template>
            </div>
        </Popover>
    </div>
</template>
<style scoped></style>
